import { EventTrackingProps } from "@/types/common";
import { getRuntimeVariables } from "@/utils";
import React, { useCallback, useEffect, useState } from "react";

interface CardBannerContent {
  heading: string;
  description: string[];
}

interface CardData {
  imagePath: string;
  bannerContent: CardBannerContent;
  buttonText: string;
  bannerClass: "banner-one" | "banner-two" | "banner-three";
  bannerUrl: string;
}

interface BannerProps {
  pageName: string;
  imgBasePath: string;
  handleEventTracking: (props: EventTrackingProps) => void;
}

const BannerTailwind: React.FC<BannerProps> = ({
  pageName,
  imgBasePath,
  handleEventTracking,
}) => {
  const host = window.location.host;
  const isProd = getRuntimeVariables(`${host}`);
  const isLocalDevelopment =
    typeof window !== "undefined" && (window as any).localDevelopment;

  const cardData: CardData[] = [
    {
      imagePath: "cs-banner-budge-1.png",
      bannerContent: {
        heading: "What it feels like to have 48% more interview calls?",
        description: ["To get 5X more recruiter views on your profile"],
      },
      buttonText: "Ask here",
      bannerClass: "banner-one",
      bannerUrl: isProd
        ? "/career-services/talk-to-us?utm_source=nudge_homepage&utm_campaign=careerservices"
        : "/web/en/talk-to-us?utm_source=nudge_homepage&utm_campaign=careerservices",
    },
    {
      imagePath: "cs-banner-budge-2.png",
      bannerContent: {
        heading: "Get your profile to rank on top of recruiter searches",
        description: [
          "Unleash your profile's true capability to 10X your chances",
        ],
      },
      buttonText: "Get now",
      bannerClass: "banner-two",
      bannerUrl: isProd
        ? "/career-services/profile-highlighter?utm_source=nudge_homepage&utm_campaign=careerservices"
        : "/web/en/profile-highlighter?utm_source=nudge_homepage&utm_campaign=careerservices",
    },
    {
      imagePath: "cs-banner-budge-3.png",
      bannerContent: {
        heading: "How to stand out from the millions of other resumes?",
        description: [
          "80% of the resumes are rejected in 11 seconds.",
          "Don't want to be the 80%?",
        ],
      },
      buttonText: "Ask here",
      bannerClass: "banner-three",
      bannerUrl: isProd
        ? "/career-services/profile-highlighter?utm_source=nudge_homepage&utm_campaign=careerservices"
        : "/web/en/profile-highlighter?utm_source=nudge_homepage&utm_campaign=careerservices",
    },
  ];

  const [currentCard, setCurrentCard] = useState<number>(0);

  useEffect(() => {
    const changeBanner = setInterval(() => {
      setCurrentCard((prev) => (prev < 2 ? prev + 1 : 0));
    }, 7000);
    return () => clearInterval(changeBanner);
  }, []);

  const getImagePath = useCallback(
    (imageName: string) => {
      return isLocalDevelopment
        ? `../srp/${imageName}`
        : `${imgBasePath}${imageName}`;
    },
    [imgBasePath, isLocalDevelopment],
  );

  const redirectCareerServices = (url: string) => {
    if (url && url !== "") {
      handleEventTracking({
        eventName: "seekerservicebanners",
        eventType: "eventPush",
        eventProps: {
          page_name: pageName,
        },
      });
      handleEventTracking({
        eventName: "Banner Section Clicked",
        eventType: "eventPush",
        eventProps: {
          page_name: "career service",
          sectionName: "srp-result",
        },
      });
    }
  };

  const bannerTypeClasses: Record<
    CardData["bannerClass"],
    {
      container: string;
      imageHolder: string;
      descHeader: string;
      descBody: string;
    }
  > = {
    "banner-one": {
      container: "bg-[#fff8e9] border border-[#f2e7d0]",
      imageHolder: "absolute bottom-0 left-[19px] w-[185px]",
      descHeader: "w-[97%] text-[23px] leading-[27px] font-semibold",
      descBody: "mt-[5px] mb-[80px]",
    },
    "banner-two": {
      container: "bg-[#eff8ec] border border-[#cee5c6]",
      imageHolder: "absolute bottom-[35px] right-0 w-[132px]",
      descHeader: "text-[20px] font-semibold leading-[27px]",
      descBody: "w-[60%] mt-[5px] mb-[33px]",
    },
    "banner-three": {
      container: "bg-[#fcfaff] border border-[#d4c6f4]",
      imageHolder: "absolute top-[9px] right-[40px] w-[111px]",
      descHeader: "w-[55%] text-[20px] leading-[22px]",
      descBody: "mt-[26px] mb-[17px]",
    },
  };

  return (
    <div className="w-[100%]">
      {cardData.map((card, index) => {
        const typeClasses = bannerTypeClasses[card.bannerClass];
        return (
          <button
            key={index}
            className={`relative mb-8 mt-4 h-[206px] cursor-pointer p-5 md:!mt-0 md:mb-4 ${typeClasses.container} w-full text-left`}
            style={{ display: index === currentCard ? "block" : "none" }}
            onClick={() => redirectCareerServices(card.bannerUrl)}
          >
            <a
              href={card.bannerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-current no-underline"
            >
              <div className={typeClasses.imageHolder}>
                <img
                  src={getImagePath(`images/${card.imagePath}`)}
                  alt={card.imagePath}
                  className="w-full"
                />
              </div>
              <div className="relative">
                <div className={typeClasses.descHeader}>
                  {card.bannerContent.heading}
                </div>
                <div className={typeClasses.descBody}>
                  {card.bannerContent.description.map((sentence, sIndex) => (
                    <p key={sIndex} className="text-[15px] font-semibold">
                      {sentence}
                    </p>
                  ))}
                </div>
              </div>
              <div className="bg-primary-1 border-primary-1 absolute bottom-0 right-0 flex h-[38px] w-[133px] items-center justify-center rounded-tl-[38px] border bg-[#6E00BE] px-4 py-2 text-sm leading-[19px] text-white">
                {card.buttonText}
              </div>
            </a>
          </button>
        );
      })}
    </div>
  );
};

export default BannerTailwind;
